import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Sparkles, Music, Mic, ArrowRight } from "lucide-react";

const LANGUAGES = {
  english: { label: "English", native: "English", genreHint: "Pop, Hip-Hop, EDM, Cinematic", usecases: ["D2C premium brands", "SaaS product launches", "Mobile app promos", "International ad campaigns"] },
  hindi: { label: "Hindi", native: "हिंदी", genreHint: "Bollywood, Pop, Classical Fusion", usecases: ["Bollywood-style brand films", "Festive ads (Diwali, Holi)", "Coaching institute jingles", "National TV campaigns"] },
  hinglish: { label: "Hinglish", native: "Hinglish", genreHint: "Pop, Hip-Hop, EDM", usecases: ["D2C youth brands", "Instagram Reels hooks", "Quick-commerce promotions", "Urban millennial-targeted ads"] },
  tamil: { label: "Tamil", native: "தமிழ்", genreHint: "Cinematic, Classical Fusion, Pop", usecases: ["Tamil Nadu local brands", "Kollywood-style trailers", "Coimbatore textile ads", "Chennai real estate"] },
  telugu: { label: "Telugu", native: "తెలుగు", genreHint: "Cinematic, Dance, Pop", usecases: ["Hyderabad D2C brands", "Tollywood promos", "Andhra-Telangana retail", "Regional OTT launches"] },
  bengali: { label: "Bengali", native: "বাংলা", genreHint: "Indie Folk, Acoustic, Cinematic", usecases: ["Kolkata brand campaigns", "Durga Puja sponsorships", "Bengali fashion & jewelry", "West Bengal FMCG"] },
  marathi: { label: "Marathi", native: "मराठी", genreHint: "Pop, Cinematic, Classical Fusion", usecases: ["Mumbai-Pune local ads", "Ganesh Chaturthi campaigns", "Maharashtra real estate", "Marathi TV channel promos"] },
  gujarati: { label: "Gujarati", native: "ગુજરાતી", genreHint: "Bollywood, Pop, Kids", usecases: ["Diamond & jewelry brands", "Textile & handicraft ads", "Navratri campaigns", "Gujarat retail & grocery"] },
  punjabi: { label: "Punjabi", native: "ਪੰਜਾਬੀ", genreHint: "Punjabi Pop, EDM, Hip-Hop", usecases: ["Wedding brands", "Nightlife & fashion", "Agri-tech for Punjab", "NRI-targeted campaigns"] },
  kannada: { label: "Kannada", native: "ಕನ್ನಡ", genreHint: "Classical Fusion, Pop, Cinematic", usecases: ["Bengaluru startups", "Karnataka retail", "Sandalwood-style trailers", "Mangalore coastal brands"] },
  malayalam: { label: "Malayalam", native: "മലയാളം", genreHint: "Indie Folk, Classical Fusion, Cinematic", usecases: ["Kerala tourism & hospitality", "Mollywood-style promos", "Kochi D2C brands", "Onam festival campaigns"] },
};

export default function JingleByLang() {
  const { lang } = useParams();
  const cfg = LANGUAGES[lang];
  if (!cfg) return <Navigate to="/" replace />;

  const title = `${cfg.label} Jingle Maker`;

  return (
    <div className="min-h-screen bg-white" data-testid={`lang-page-${lang}`}>
      <Navbar />

      <section className="relative border-b border-black/10">
        <div className="absolute inset-0 grid-bg opacity-60 pointer-events-none" />
        <div className="relative max-w-5xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <div className="inline-flex items-center gap-2 px-2.5 py-1 border border-black/15 text-[11px] uppercase tracking-[0.2em] font-bold mb-6">
            <span className="w-1.5 h-1.5 bg-[#FF4500] rounded-full animate-pulse" />
            {cfg.native} · AI Jingle Generator
          </div>
          <h1 className="font-display text-5xl md:text-7xl font-semibold tracking-tighter leading-[0.95] text-balance">
            {title}. <br />
            <span className="text-[#FF4500]">Catchy songs</span> in {cfg.native}.
          </h1>
          <p className="mt-6 text-lg md:text-xl text-zinc-600 max-w-2xl leading-relaxed">
            Turn any ad script into a production-ready {cfg.label} jingle in under 90 seconds. Real vocals, real music — not text-to-speech.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-4">
            <Link to="/login">
              <Button className="bg-[#FF4500] hover:bg-[#E03C00] text-white rounded-sm h-12 px-6 text-base font-semibold" data-testid="lang-cta-btn">
                Generate Your First Free Jingle
                <Sparkles size={18} className="ml-2" />
              </Button>
            </Link>
            <Link to="/pricing" className="text-sm font-semibold underline underline-offset-4 hover:text-[#FF4500]">
              See pricing →
            </Link>
          </div>
        </div>
      </section>

      <section className="py-20 border-b border-black/10">
        <div className="max-w-5xl mx-auto px-6 md:px-10 grid md:grid-cols-3 gap-8">
          <div>
            <div className="w-12 h-12 border border-black/10 flex items-center justify-center mb-5">
              <Mic size={20} />
            </div>
            <h3 className="font-display text-xl font-semibold mb-2">Authentic vocals</h3>
            <p className="text-zinc-600 text-sm leading-relaxed">
              Male or female singers with authentic {cfg.label} pronunciation — trained on regional music traditions.
            </p>
          </div>
          <div>
            <div className="w-12 h-12 border border-black/10 flex items-center justify-center mb-5">
              <Music size={20} />
            </div>
            <h3 className="font-display text-xl font-semibold mb-2">Best-fit genres</h3>
            <p className="text-zinc-600 text-sm leading-relaxed">
              {cfg.genreHint} — pre-tuned for how {cfg.label} ads actually sound on TV, reels, and radio.
            </p>
          </div>
          <div>
            <div className="w-12 h-12 border border-black/10 flex items-center justify-center mb-5">
              <ArrowRight size={20} />
            </div>
            <h3 className="font-display text-xl font-semibold mb-2">Script → Jingle</h3>
            <p className="text-zinc-600 text-sm leading-relaxed">
              Paste a script, or let our AI write one from a product brief. Edit freely before generating.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 border-b border-black/10 bg-[#F8F9FA]">
        <div className="max-w-5xl mx-auto px-6 md:px-10">
          <div className="text-xs uppercase tracking-widest text-[#FF4500] font-bold mb-4">Perfect for</div>
          <h2 className="font-display text-3xl md:text-4xl font-semibold tracking-tighter leading-tight mb-10">
            {cfg.label} businesses who want their ads to sing.
          </h2>
          <div className="grid md:grid-cols-2 gap-3">
            {cfg.usecases.map((u, i) => (
              <div key={i} className="flex items-center gap-3 p-4 bg-white border border-black/10">
                <div className="w-8 h-8 bg-[#FF4500] text-white flex items-center justify-center font-bold text-sm shrink-0">
                  {i + 1}
                </div>
                <div className="font-medium">{u}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#09090B] text-white text-center">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tighter leading-tight">
            Make your first {cfg.label} jingle — <span className="text-[#FF4500]">free</span>.
          </h2>
          <div className="mt-8">
            <Link to="/login">
              <Button className="bg-[#FF4500] hover:bg-[#E03C00] text-white rounded-sm h-12 px-6 text-base font-semibold">
                Generate Your First Free Jingle
                <Sparkles size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
